import { Box, FormHelperText } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Button, useHelperTextClasses } from '@uplift/ui-v2';
import { useMemo, useState } from 'react';
import { DiagnosisSelectedChips } from './diagnosis-selected-chips.component';
import { DiagnosisSearchModal } from './diagnosis-search-modal.modal';
import { DiagnosisSearch } from './diagnosis-search.component';
import { useL10n } from '@uplift/hooks';
import { Diagnosis } from '@uplift/types';
import { useFormikContext } from 'formik';

export type SelectedDiagnosesHashType = { [key: string]: boolean };

export type DiagnosisSelectProps = {
  diagnoses: Diagnosis[];
  name: string;
  chipClassName?: string;
  color?: 'primary' | 'secondary';
  disabled?: boolean;
  buttonTextVariant?: boolean;
};

export const DiagnosisSelect: React.VFC<DiagnosisSelectProps> = ({
  diagnoses,
  name,
  chipClassName,
  color = 'primary',
  disabled = false,
  buttonTextVariant
}) => {
  const formik = useFormikContext();
  const { classes: helperTextClasses } = useHelperTextClasses({});
  const l10n = useL10n();
  const [ open, setOpen ] = useState(false);
  const [ visited, setVisited ] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setVisited(true);
  };

  const onDiagnosisSelect = (diagnosisId: string) => {
    formik.setValues((prev) => ({
      ...prev,
      [name]: [ ...(prev[name] ?? []), diagnosisId ]
    }));
    handleClose();
  };

  const onDiagnosisRemove = (diagnosisId: string) => {
    formik.setValues((prev) => ({
      ...prev,
      [name]: (prev[name] ?? []).filter((id) => (id !== diagnosisId)),
    }));
  };

  const selectedDiagnosesIds = useMemo(() => formik.values[name] ?? [], [ formik.values, name ]);

  // hash to optimize array searches
  const selectedDiagnosesHash = useMemo(() =>
    selectedDiagnosesIds.reduce((r, v) =>
      ({ ...r, [v]: true }),
    {} as { [key: string]: boolean }),
  [ selectedDiagnosesIds ]);

  const selectedDiagnoses = useMemo(() => {
    return diagnoses.filter((diagnosis) => {
      return (diagnosis.id in selectedDiagnosesHash);
    });
  }, [ selectedDiagnosesHash, diagnoses ]);

  const hasDiagnoses = Boolean(selectedDiagnosesIds.length);
  const error = formik.errors?.[name] ?? null;
  const showError = !!error && (visited || !!formik.touched?.[name]);

  return (
    <Box className="fs-exclude">
      {hasDiagnoses && (
        <Box mb={3}>
          <DiagnosisSelectedChips
            selectedDiagnoses={selectedDiagnoses}
            onDiagnosisRemove={onDiagnosisRemove}
            className={chipClassName}
          />
        </Box>
      )}
      <Box ml={hasDiagnoses ? -1 : 0}>
        <Button
          onClick={() => setOpen(true)}
          color={color}
          variant={hasDiagnoses || buttonTextVariant ? 'text' : 'contained'}
          startIcon={<Add />}
          disabled={disabled}
          name={name}
        >
          {l10n.clinicalNotes.addDiagnosis}
        </Button>
        {showError && (
          <Box mt={3}>
            <FormHelperText className={helperTextClasses.root} error={true}>
              {error}
            </FormHelperText>
          </Box>
        )}
      </Box>
      {open && (
        <DiagnosisSearchModal onClose={handleClose}>
          <DiagnosisSearch
            diagnoses={diagnoses}
            selectedDiagnosesHash={selectedDiagnosesHash}
            onDiagnosisSelect={onDiagnosisSelect}
            color={color}
          />
        </DiagnosisSearchModal>
      )}
    </Box>
  );
};
